import type { ModeInfo } from "./types";
import { FALLBACK_MODES } from "./constants";

// PERMANENT: stored records retain legacy wire names forever, even after a
// server switches its live vocabulary to tiers.
export const LEGACY_MODE_LABELS: Record<string, string> = {
  fast: "Fast",
  thinking: "Thinking",
  pro: "Pro",
  deep_research: "Deep Research",
};

function readableModeName(name: string): string {
  const readable = name.replace(/[_-]+/g, " ").trim();
  return readable ? readable.replace(/\b\w/g, (letter) => letter.toUpperCase()) : "Unknown mode";
}

export function resolveModeLabel(name: string, modes?: readonly ModeInfo[]): string {
  const serverMode = modes?.find((mode) => mode.name === name);
  return serverMode?.label || LEGACY_MODE_LABELS[name] || readableModeName(name);
}

/** Resolves a stored composer default without mutating the saved preference. */
export function resolveDefaultMode(stored: string | null | undefined, modes: readonly ModeInfo[]): string {
  if (!stored || stored === "deep_research") return "auto";
  if (modes.length === 0) return stored;
  return stored === "auto" || modes.some((mode) => mode.name === stored) ? stored : "auto";
}

/** Unknown modes preserve the legacy transport contract: only Pro and Deep Research background. */
export function modeUsesBackground(name: string, modes?: readonly ModeInfo[]): boolean {
  const serverMode = modes?.find((mode) => mode.name === name);
  return serverMode ? serverMode.background : name === "pro" || name === "deep_research";
}

// Currently unused: timeout_s is RUN duration, not an HTTP timeout, and the poll loop is intentionally unbounded.
export function modeTimeoutSeconds(name: string, modes?: readonly ModeInfo[]): number | null {
  return modes?.find((mode) => mode.name === name)?.timeout_s ?? null;
}

/** Server order is authoritative; retain a deterministic legacy fallback while it loads. */
export function modeRank(name: string, modes?: readonly ModeInfo[]): number {
  const serverOrder = modes?.find((mode) => mode.name === name)?.order;
  if (typeof serverOrder === "number") return serverOrder;
  if (modes?.length) {
    // Match iOS: a mode unknown to this server list errs toward the heaviest
    // dispatch path, including when server orders are not contiguous.
    return Math.max(...modes.map((mode) => mode.order)) + 1;
  }
  const fallbackOrder = FALLBACK_MODES.indexOf(name as typeof FALLBACK_MODES[number]);
  return fallbackOrder === -1 ? FALLBACK_MODES.length : fallbackOrder;
}
